import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { entitySelectors } from '@store/entity/selectors';
import { settingSelectors } from '@store/setting/selectors';

import type { Property } from 'shared-types/Property';

import { getDiningRestaurants, getRestaurantAvailabilityForDate } from './requests';
import type { DiningAvailabilityTimeslot, DiningPromotion, DiningRestaurant } from './schemas';

export const diningKeys = {
  all: ['dining'] as const,
  restaurants: (propertyId: number, language: string) =>
    [...diningKeys.all, 'restaurants', propertyId, language] as const,
  availability: (
    propertyId: number,
    restaurantId: number | undefined,
    restaurantName: string | undefined,
    date: string,
    adults: number,
    children: number
  ) =>
    [
      ...diningKeys.all,
      'availability',
      propertyId,
      restaurantId,
      restaurantName,
      date,
      adults,
      children,
    ] as const,
};

export function usePropertyContext() {
  const property = useSelector(entitySelectors.getProperty) as Property | undefined;
  const language =
    (useSelector(settingSelectors.getUserLanguage) as string | undefined) ??
    property?.defaultLanguage ??
    'en';

  return { propertyId: property?.id ?? 0, language };
}

export function useDiningRestaurants() {
  const { propertyId, language } = usePropertyContext();

  return useQuery({
    queryKey: diningKeys.restaurants(propertyId, language),
    queryFn: () => getDiningRestaurants(propertyId, language),
    enabled: propertyId > 0,
  });
}

function parseIsoDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
}

export function formatIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function weekDates(activeDate: string): string[] {
  const parsed = parseIsoDate(activeDate);
  const offset = (parsed.getDay() + 6) % 7;
  const start = new Date(parsed);
  start.setDate(parsed.getDate() - offset);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return formatIsoDate(date);
  });
}

export function useRestaurantWeekAvailability(
  restaurant: DiningRestaurant | null,
  activeDate: string,
  adults: number,
  children: number
): { timeslots: DiningAvailabilityTimeslot[]; promotions: DiningPromotion[]; isLoading: boolean } {
  const { propertyId } = usePropertyContext();
  const dates = useMemo(() => weekDates(activeDate), [activeDate]);

  const queries = useQueries({
    queries: dates.map((date) => ({
      queryKey: diningKeys.availability(
        propertyId,
        restaurant?.restaurantId,
        restaurant?.restaurantName,
        date,
        adults,
        children
      ),
      queryFn: () =>
        getRestaurantAvailabilityForDate(propertyId, {
          date,
          restaurantName: restaurant?.restaurantName ?? '',
          restaurantId: restaurant?.restaurantId ?? 0,
          adults,
          children,
        }),
      enabled: Boolean(restaurant) && propertyId > 0,
    })),
  });

  const timeslots = queries.flatMap((query) => query.data?.timeslots ?? []);
  const isLoading = queries.some((query) => query.isLoading);

  const promotions: DiningPromotion[] = [];
  const seenPromotionIds = new Set<DiningPromotion['id']>();
  queries.forEach((query) => {
    (query.data?.promotions ?? []).forEach((promotion) => {
      if (!seenPromotionIds.has(promotion.id)) {
        seenPromotionIds.add(promotion.id);
        promotions.push(promotion);
      }
    });
  });

  return { timeslots, promotions, isLoading };
}
